import {
  TRESPONSE_LLBoardListing,
  TRESPONSE_LL_SHIFT,
  TRESPONSE_LL_TASK_MANAGER,
  TRESPONSE_LL_LEARNING_CURVE_DETAILS,
  TRESPONSE_LL_LEARNING_CURVE_BY_FACTORY_OTL,
  TRESPONSE_LINE_LEVEL_STRIP_LIST,
  TRESPONSE_LINE_STRIP_PLANNED_NUMBERS,
  TRESPONSE_LL_LOAD_ORDER_THRESHOLD,
  TLlBoardVersion,
} from '@monorepo/types';

import { TLLRectDropElement } from '@monorepo/ui';

export type TLLBoardState = {
  lineShiftInfo: TRESPONSE_LL_SHIFT | null;
  llBoardList: TRESPONSE_LLBoardListing | null;
  loadStripOnMouse: null | TLLRectDropElement[];
  llTaskManager: TRESPONSE_LL_TASK_MANAGER | null;
  learningCurveStripInfo: TRESPONSE_LL_LEARNING_CURVE_DETAILS | null;
  learningCurveByFactoryOtl: TRESPONSE_LL_LEARNING_CURVE_BY_FACTORY_OTL | null;
  lineStripListingInfo: TRESPONSE_LINE_LEVEL_STRIP_LIST | null;
  lineStripPlannedNumbers: TRESPONSE_LINE_STRIP_PLANNED_NUMBERS | null;
  llPCDPEDThreshold: TRESPONSE_LL_LOAD_ORDER_THRESHOLD | null;
  planVersions: TLlBoardVersion[];
  selectedPlanVersion: string | null;
  isCreatingPlanVersion: boolean;
  isPromotingPlanVersion: boolean;
};

export type { TWithoutPlanVersion } from '@monorepo/types';

export const PLAN_VERSION_MISSING_ERROR = 'No LL board version is selected. Please refresh the page and try again.';

export const TLLBoardInfoActionType = {
  GetLLBoardList: 'GET_LL_BOARD_LIST' as const,
  GetLLBoardListSuccess: 'GET_LL_BOARD_LIST_SUCCESS' as const,
  GetLineShiftInfo: 'GET_LINE_SHIFT_INFO' as const,
  GetLineShiftInfoSuccess: 'GET_LINE_SHIFT_INFO_SUCCESS' as const,
  GetLineShiftInfoFailure: 'GET_LINE_SHIFT_INFO_FAILURE' as const,
  AddEditLineWorkingHours: 'ADD_EDIT_LINE_WORKING_HOURS' as const,
  AddEditLineWorkingHoursSuccess: 'ADD_EDIT_LINE_WORKING_HOURS_SUCCESS' as const,
  AddEditLineWorkingHoursFailure: 'ADD_EDIT_LINE_WORKING_HOURS_FAILURE' as const,
  ResetLineWorkingHours: 'RESET_LINE_WORKING_HOURS' as const,
  ResetLineWorkingHoursSuccess: 'RESET_LINE_WORKING_HOURS_SUCCESS' as const,
  ResetLineWorkingHoursFailure: 'RESET_LINE_WORKING_HOURS_FAILURE' as const,
  SetLoadStripOnMouse: 'LOAD_LL_BOARD_STRIP' as const,
  GetLLBoardTaskManager: 'GET_LL_BOARD_TASK_MANAGER' as const,
  GetLLBoardTaskManagerSuccess: 'GET_LL_BOARD_TASK_MANAGER_SUCCESS' as const,
  GetLLBoardTaskManagerFailure: 'GET_LL_BOARD_TASK_MANAGER_FAILURE' as const,
  GetLearningCurveStripeInfo: 'GET_LEARNING_CURVE_STRIPE_INFO' as const,
  GetLearningCurveStripeInfoSuccess: 'GET_LEARNING_CURVE_STRIPE_INFO_SUCCESS' as const,
  GetLearningCurveStripeInfoFailure: 'GET_LEARNING_CURVE_STRIPE_INFO_FAILURE' as const,
  UpdateLearningCurveDayCount: 'UPDATE_LEARNING_CURVE_DAY_COUNT' as const,
  UpdateLearningCurveDayCountSuccess: 'UPDATE_LEARNING_CURVE_DAY_COUNT_SUCCESS' as const,
  UpdateLearningCurveDayCountFailure: 'UPDATE_LEARNING_CURVE_DAY_COUNT_FAILURE' as const,
  AddEditLearningCurveStrip: 'ADD_EDIT_LEARNING_CURVE_STRIP' as const,
  AddEditLearningCurveStripSuccess: 'ADD_EDIT_LEARNING_CURVE_STRIP_SUCCESS' as const,
  AddEditLearningCurveStripFailure: 'ADD_EDIT_LEARNING_CURVE_STRIP_FAILURE' as const,
  DeleteLearningCurveStrip: 'DELETE_LEARNING_CURVE_STRIP' as const,
  DeleteLearningCurveStripSuccess: 'DELETE_LEARNING_CURVE_STRIP_SUCCESS' as const,
  DeleteLearningCurveStripFailure: 'DELETE_LEARNING_CURVE_STRIP_FAILURE' as const,
  GetLearningCurveByFactoryOtl: 'GET_LEARNING_CURVE_BY_FACTORY_OTL' as const,
  GetLearningCurveByFactoryOtlSuccess: 'GET_LEARNING_CURVE_BY_FACTORY_OTL_SUCCESS' as const,
  GetLearningCurveByFactoryOtlFailure: 'GET_LEARNING_CURVE_BY_FACTORY_OTL_FAILURE' as const,
  AddEditLearningCurveByFactoryOtl: 'ADD_EDIT_LEARNING_CURVE_BY_FACTORY_OTL' as const,
  AddEditLearningCurveByFactoryOtlSuccess: 'ADD_EDIT_LEARNING_CURVE_BY_FACTORY_OTL_SUCCESS' as const,
  AddEditLearningCurveByFactoryOtlFailure: 'ADD_EDIT_LEARNING_CURVE_BY_FACTORY_OTL_FAILURE' as const,
  GetLineStripListing: 'GET_LINE_LEVEL_STRIP_LISTING' as const,
  GetLineStripListingSuccess: 'GET_LINE_LEVEL_STRIP_LISTING_SUCCESS' as const,
  GetLineStripListingFailure: 'GET_LINE_LEVEL_STRIP_LISTING_FAILURE' as const,
  LineStripPlannedNumbers: 'LINE_STRIP_PLANNED_NUMBERS' as const,
  LineStripPlannedNumbersSuccess: 'LINE_STRIP_PLANNED_NUMBERS_SUCCESS' as const,
  LineStripPlannedNumbersFailure: 'LINE_STRIP_PLANNED_NUMBERS_FAILURE' as const,
  UnloadStripMovePartially: 'UNLOAD_STRIP_MOVE_PARTYIALLY' as const,
  UnloadStripMovePartiallySuccess: 'UNLOAD_STRIP_MOVE_PARTYIALLY_SUCCESS' as const,
  UnloadStripMovePartiallyFailure: 'UNLOAD_STRIP_MOVE_PARTYIALLY_FAILURE' as const,
  AddEditNoteOnStrip: 'LINE_LEVEL_STRIP_ADD_EDIT_NOTE' as const,
  AddEditNoteOnStripSuccess: 'LINE_LEVEL_STRIP_ADD_EDIT_NOTE_SUCCESS' as const,
  AddEditNoteOnStripFailure: 'LINE_LEVEL_STRIP_ADD_EDIT_NOTE_FAILURE' as const,
  AddEditLineWorkForceCount: 'ADD_EDIT_LINE_WORKFORCE_COUNT' as const,
  AddEditLineWorkForceCountSuccess: 'ADD_EDIT_LINE_WORKFORCE_COUNT_SUCCESS' as const,
  AddEditLineWorkForceCountFailure: 'ADD_EDIT_LINE_WORKFORCE_COUNT_FAILURE' as const,
  ResetLineWorkForceCount: 'RESET_LINE_WORKFORCE_COUNT' as const,
  ResetLineWorkForceCountSuccess: 'RESET_LINE_WORKFORCE_COUNT_SUCCESS' as const,
  ResetLineWorkForceCountFailure: 'RESET_LINE_WORKFORCE_COUNT_FAILURE' as const,
  SetLLPCDPEDThreshold: 'SET_LL_PCD_PED_THRESHOLD' as const,
  LoadPlanVersions: 'LOAD_LL_PLAN_VERSIONS' as const,
  LoadPlanVersionsSuccess: 'LOAD_LL_PLAN_VERSIONS_SUCCESS' as const,
  LoadPlanVersionsFailure: 'LOAD_LL_PLAN_VERSIONS_FAILURE' as const,
  SetPlanVersions: 'SET_LL_PLAN_VERSIONS' as const,
  SetSelectedPlanVersion: 'SET_LL_SELECTED_PLAN_VERSION' as const,
  SetIsCreatingPlanVersion: 'SET_LL_IS_CREATING_PLAN_VERSION' as const,
  SetIsPromotingPlanVersion: 'SET_LL_IS_PROMOTING_PLAN_VERSION' as const,
};
